import { AvatarV2, SelectV2, SelectV2Option } from '@cimplico/components';
import { Ref, useEffect, useMemo, useState } from 'react';
import { useFirmInvitations } from '../../queries/use-firm-invitations';
import { useUsers } from '../../queries/use-users';
import { User } from '../../types/user';
import { WorkpaperGuest, WorkpaperRoleEnum } from '../../types/workpaper';
import plural from '../../utilities/plural';

type WorkpaperRoleSelectProps = {
    canInvite?: boolean;
    disabledUsers?: { id?: string; email?: string }[];
    isOptional?: boolean;
    isMulti?: boolean;
    isDisabled?: boolean;
    isCreatable?: boolean;
    form?: string;
    errorMessage?: string;
    disabledHint?: string;
    innerRef?: Ref<HTMLInputElement>;
    defaultValue?: WorkpaperGuest[];
    title: string;
    placeholder?: string;
    role: WorkpaperRoleEnum;
    onChange: (users: WorkpaperGuest[]) => void;
    onExternalUserError?: (hasError: boolean) => void;
};

function WorkpaperRoleSelect({
    canInvite = false,
    disabledUsers = [],
    isMulti = false,
    isOptional = false,
    isDisabled = false,
    isCreatable = true,
    form,
    errorMessage,
    disabledHint,
    innerRef,
    defaultValue,
    title,
    placeholder,
    role,
    onChange,
    onExternalUserError,
}: WorkpaperRoleSelectProps): JSX.Element {
    const [userSearch, setUserSearch] = useState('');
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<SelectV2Option[]>([]);
    const [error, setError] = useState<string | undefined>(errorMessage);

    const usersQuery = useUsers({
        page: 1,
        search: `${userSearch}&searchFields=name&searchFields=email`,
        sort: '',
        filter: role === WorkpaperRoleEnum.GUEST ? 'role=EXTERNAL_USER' : '',
        searchFilters: 'searchFilters=role',
    });

    const invitationsQuery = useFirmInvitations({
        page: 1,
        limit: 999,
        search: userSearch,
        sort: '',
        filter: 'role=EXTERNAL_USER',
        searchFilters: 'searchFilters=role',
    });

    useEffect(() => {
        if (usersQuery.data?.users && !userSearch) {
            setAllUsers(usersQuery.data?.users);
        }
    }, [usersQuery.data?.users]);

    useEffect((): void => {
        if (errorMessage) {
            handleErrorMessage(errorMessage);
        }
    }, [errorMessage]);

    function handleErrorMessage(message?: string): void {
        onExternalUserError && onExternalUserError(!!message);
        setError(message);
    }

    function handleGuestsSelect(options: SelectV2Option[]): void {
        handleErrorMessage(undefined);

        // Check if all email addresses are unique
        const uniqueEmails = Array.from(
            new Set(
                options.map((o) => {
                    if (o.description) {
                        return o.description;
                    }
                    return o.value;
                })
            )
        );

        if (options.length !== uniqueEmails.length) {
            handleErrorMessage("You can't invite users with the same email address.");
        }

        // Check if all email addresses are valid
        const invalidGuests = options.filter(
            (guest) => !allUsers.find((g) => g.id === guest.value) && !/.+@.+\..+\.*.*[^.]$/gim.test(guest.value as string)
        );

        if (invalidGuests.length > 0) {
            handleErrorMessage(
                `Unable to invite new ${plural(invalidGuests.length, 'user', 'users')} with ${plural(
                    invalidGuests.length,
                    'this',
                    'these'
                )} email ${plural(invalidGuests.length, 'address', 'addresses')}: ${invalidGuests
                    .map((invalid) => invalid.value)
                    .join(', ')}`
            );
        }

        const selectedGuests: WorkpaperGuest[] = options.map((guest) => {
            const matchingUser = allUsers?.find((u) => u.id === guest.value);

            if (matchingUser) {
                return {
                    id: matchingUser.id,
                    firstName: matchingUser.firstName,
                    lastName: matchingUser.lastName,
                    email: matchingUser.email,
                    avatarUrl: matchingUser.avatarUrl,
                    profileHexColour: matchingUser.profileHexColour,
                    role,
                };
            } else {
                return { email: guest.value as string, role };
            }
        });

        onChange(selectedGuests);
        setSelectedUsers(options);
        setUserSearch('');
    }

    useEffect(() => {
        if (defaultValue && defaultValue.length > 0) {
            const defaultSelected: SelectV2Option[] = defaultValue.map((val) => {
                return {
                    label: val.firstName && val.lastName ? `${val.firstName} ${val.lastName}` : val.email,
                    description: val.id && val.email,
                    value: val.id || val.email,
                    decoration: (
                        <AvatarV2
                            alt={val.firstName && val.lastName ? val.firstName[0] + val.lastName[0] : undefined}
                            size="mini"
                            color={val.profileHexColour}
                        />
                    ),
                };
            });
            setSelectedUsers(defaultSelected);
        }
    }, [defaultValue]);

    const options = useMemo(() => {
        let existingUsers: SelectV2Option[] = (usersQuery?.data?.users || []).map((u) => ({
            isDisabled: disabledUsers.find((user) => user.id === u.id) !== undefined,
            label: `${u.firstName} ${u.lastName}`,
            description: u.email,
            value: u.id,
            decoration: <AvatarV2 alt={u.firstName[0] + u.lastName[0]} size="mini" color={u.profileHexColour} />,
        }));

        if (canInvite) {
            const invitedUsers: SelectV2Option[] = (invitationsQuery.data?.invitations || []).map((u) => ({
                isDisabled: disabledUsers.find((user) => user.email === u.email) !== undefined,
                label: u.email,
                value: u.email,
                decoration: <AvatarV2 size="mini" />,
            }));

            existingUsers = [...existingUsers, ...invitedUsers];
        }

        return existingUsers;
    }, [invitationsQuery.data?.invitations, usersQuery?.data?.users, disabledUsers]);

    return (
        <div>
            <SelectV2
                size="standard"
                newOptionActionLabel={canInvite ? 'Invite' : undefined}
                placeholder={placeholder || 'Select user(s)...'}
                title={title}
                form={form}
                isOptional={isOptional}
                isMulti={isMulti}
                isCreatable={isCreatable}
                disabled={isDisabled}
                disabledHint={disabledHint}
                ref={innerRef}
                errorMessage={error}
                value={selectedUsers}
                isLoading={usersQuery.isLoading || usersQuery.isFetching}
                onSearch={setUserSearch}
                onChange={handleGuestsSelect}
                options={options}
            />
        </div>
    );
}
export default WorkpaperRoleSelect;
